// src/pages/UmraGuidePage/UmraGuidePage.tsx
import React from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Navigation from '../../components/Navigation/Navigation';
import guideUmraData from '../../data/guide-umra.json';
import './UmraGuidePage.css';
import SEO from '../../components/SEO/SEO';

interface InvocationItem {
  arabic: string;
  phonetic: string;
  translation: string;
}

interface InvocationMethodItem {
  title: string;
  description?: string;
  invocation?: InvocationItem;
  instruction?: string;
}

interface EnumerationItem {
  label: string;
  text: string;
  subtext?: string;
}

interface HadithItem {
  text: string;
  source?: string;
  context?: string;
  reference?: string;
}

interface ContentItem {
  type: string;
  text?: string;
  number?: number;
  label?: string;
  arabic?: string;
  phonetic?: string;
  translation?: string;
  reference?: string;
  source?: string;
  context?: string;
  items?: string[] | EnumerationItem[];
  methods?: InvocationMethodItem[];
  hadiths?: HadithItem[];
  conclusion?: string;
  mainMessage?: string;
  title?: string;
  content?: string;
}

interface GuideData {
  title: string;
  subtitle: string;
  author: string;
  description: string;
  conference_info: {
    title_ar: string;
    location: string;
    date: string;
  };
  content: ContentItem[];
}

const UmraGuidePage: React.FC = () => {
  const data = guideUmraData as GuideData;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track quelle partie on est en train de rendre
  let currentPart = 0;

  const renderContentItem = (item: ContentItem, index: number) => {
    // Mettre à jour le numéro de partie
    if (item.type === 'part-title') {
      currentPart = item.number || 0;
    }

    switch (item.type) {
      case 'introduction':
        return (
          <div key={index} className="umra-introduction">
            <p>{item.text}</p>
          </div>
        );

      case 'part-title':
        return (
          <div key={index} className={`umra-part-title part-${item.number}`}>
            <div className="umra-part-number">{item.number}</div>
            <h2 className="umra-part-text">{item.text}</h2>
          </div>
        );

      case 'section-title':
        return (
          <h3 key={index} className="umra-section-title">
            <span className="umra-section-number">{item.number}.</span>
            {item.text}
          </h3>
        );

      case 'subsection-title':
        return <h4 key={index} className="umra-subsection-title">{item.text}</h4>;

      case 'paragraph':
        const renderParagraphContent = (text: string) => {
          // Détecter "Pour l'homme :" ou "Pour la femme :"
          if (text.includes('Pour l\'homme :') || text.includes('Pour les hommes :') || 
              text.includes('Pour la femme :') || text.includes('Pour les femmes :')) {
            const parts = text.split(/(:)/);
            return (
              <p>
                <strong className="umra-paragraph-gender">{parts[0]}{parts[1]}</strong>
                {parts.slice(2).join('')}
              </p>
            );
          }
          
          // Détecter les phrases d'introduction (finissant par ":")
          if (text.trim().endsWith(':') || text.includes('Il est recommandé')) {
            return <p className="umra-paragraph-intro">{text}</p>;
          }
          
          return <p>{text}</p>;
        };

        return (
          <div key={index} className="umra-paragraph">
            {item.text?.split('\n').map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {renderParagraphContent(line)}
              </React.Fragment>
            ))}
          </div>
        );

      case 'list':
        return (
          <ul key={index} className="umra-list">
            {(item.items as string[])?.map((listItem, listIndex) => (
              <li key={listIndex}>{listItem}</li>
            ))}
          </ul>
        );

      case 'highlight':
        return (
          <div key={index} className="umra-highlight">
            {item.label && <div className="umra-highlight-label">{item.label}</div>}
            <div className="umra-highlight-text">
              {item.text?.split('\n').map((line, lineIndex) => (
                <p key={lineIndex}>{line}</p>
              ))}
            </div>
          </div>
        );

      case 'important-note':
        return (
          <div key={index} className="umra-important-note">
            <div className="umra-note-icon">⚠️</div>
            <div className="umra-note-text">{item.text}</div>
          </div>
        );

      case 'invocation':
        return (
          <div key={index} className={`umra-invocation-card part-${currentPart}`}>
            <div className="umra-invocation-header">
              <div className="umra-invocation-icon">🤲</div>
              <div className="umra-invocation-title">Invocation</div>
            </div>
            <div className="umra-invocation-body">
              <div className="umra-invocation-arabic">{item.arabic}</div>
              <div className="umra-invocation-phonetic">{item.phonetic}</div>
              <div className="umra-invocation-translation">{item.translation}</div>
            </div>
          </div>
        );

      case 'invocation-choice':
        return (
          <div key={index} className={`umra-invocation-card part-${currentPart}`}>
            <div className="umra-invocation-header">
              <div className="umra-invocation-icon">🤲</div>
              <div className="umra-invocation-title">{item.label || 'Invocation'}</div>
            </div>
            <div className="umra-invocation-body">
              <div className="umra-invocation-arabic">{item.arabic}</div>
              <div className="umra-invocation-phonetic">{item.phonetic}</div>
              <div className="umra-invocation-translation">{item.translation}</div>
            </div>
          </div>
        );

      case 'verse':
        return (
          <div key={index} className={`umra-verse-card part-${currentPart}`}>
            <div className="umra-verse-header">
              <div className="umra-verse-icon">📖</div>
              <div className="umra-verse-title">Verset coranique</div>
            </div>
            <div className="umra-verse-body">
              <div className="umra-verse-arabic">{item.arabic}</div>
              <div className="umra-verse-phonetic">{item.phonetic}</div>
              <div className="umra-verse-translation">{item.translation}</div>
              {item.reference && (
                <div className="umra-verse-reference">{item.reference}</div>
              )}
            </div>
          </div>
        );

      case 'hadith':
        return (
          <div key={index} className="umra-hadith-card">
            <div className="umra-hadith-header">
              <div className="umra-hadith-icon">📜</div>
              <div className="umra-hadith-title">Hadith</div>
            </div>
            <div className="umra-hadith-body">
              <div className="umra-hadith-text">« {item.text} »</div>
              {item.source && (
                <div className="umra-hadith-source">— {item.source}</div>
              )}
              {item.context && (
                <div className="umra-hadith-context">{item.context}</div>
              )}
              {item.reference && (
                <div className="umra-hadith-reference">{item.reference}</div>
              )}
            </div>
          </div>
        );

      case 'hadith-multiple':
        return (
          <div key={index} className="umra-hadith-card">
            <div className="umra-hadith-header">
              <div className="umra-hadith-icon">📜</div>
              <div className="umra-hadith-title">Hadiths</div>
            </div>
            <div className="umra-hadith-body">
              <div className="umra-hadith-multiple">
                {item.hadiths?.map((hadith, hadithIndex) => (
                  <div key={hadithIndex} className="umra-hadith-item">
                    <div className="umra-hadith-number">Hadith {hadithIndex + 1}</div>
                    <div className="umra-hadith-text">« {hadith.text} »</div>
                    {hadith.source && (
                      <div className="umra-hadith-source">— {hadith.source}</div>
                    )}
                    {hadith.context && (
                      <div className="umra-hadith-context">{hadith.context}</div>
                    )}
                    {hadith.reference && (
                      <div className="umra-hadith-reference">{hadith.reference}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'athar':
        return (
          <div key={index} className="umra-athar-card">
            <div className="umra-athar-header">
              <div className="umra-athar-icon">📜</div>
              <div className="umra-athar-title">Athar</div>
            </div>
            <div className="umra-athar-body">
              <div className="umra-athar-text">« {item.text} »</div>
              {item.source && (
                <div className="umra-athar-source">— {item.source}</div>
              )}
              {item.context && (
                <div className="umra-athar-context">{item.context}</div>
              )}
              {item.reference && (
                <div className="umra-athar-reference">{item.reference}</div>
              )}
            </div>
          </div>
        );

      case 'athar-multiple':
        return (
          <div key={index} className="umra-athar-card">
            <div className="umra-athar-header">
              <div className="umra-athar-icon">📜</div>
              <div className="umra-athar-title">Athar</div>
            </div>
            <div className="umra-athar-body">
              <div className="umra-athar-multiple">
                {item.hadiths?.map((athar, atharIndex) => (
                  <div key={atharIndex} className="umra-athar-item">
                    <div className="umra-athar-item-header">
                      <div className="umra-athar-number">Narration {atharIndex + 1}</div>
                      {athar.context && (
                        <div className="umra-athar-item-context">{athar.context}</div>
                      )}
                    </div>
                    <div className="umra-athar-text">« {athar.text} »</div>
                    {athar.source && (
                      <div className="umra-athar-source">— {athar.source}</div>
                    )}
                    {athar.reference && (
                      <div className="umra-athar-reference">{athar.reference}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'enumeration':
        return (
          <div key={index} className={`umra-enumeration part-${currentPart}`}>
            {(item.items as EnumerationItem[])?.map((enumItem, enumIndex) => (
              <div key={enumIndex} className="umra-enum-item">
                <div className="umra-enum-label">{enumItem.label}</div>
                <div className="umra-enum-content">
                  <p className="umra-enum-text">{enumItem.text}</p>
                  {enumItem.subtext && (
                    <p className="umra-enum-subtext">{enumItem.subtext}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      case 'invocation-methods':
        return (
          <div key={index} className={`umra-invocation-methods part-${currentPart}`}>
            {item.methods?.map((method, methodIndex) => (
              <div key={methodIndex} className="umra-method-card">
                <div className="umra-method-header">
                  <div className="umra-method-number">{methodIndex + 1}</div>
                  <h5 className="umra-method-title">{method.title}</h5>
                </div>
                {method.description && (
                  <p className="umra-method-description">{method.description}</p>
                )}
                {method.invocation && (
                  <div className="umra-method-invocation">
                    <div className="umra-invocation-arabic">{method.invocation.arabic}</div>
                    <div className="umra-invocation-phonetic">{method.invocation.phonetic}</div>
                    <div className="umra-invocation-translation">{method.invocation.translation}</div>
                  </div>
                )}
                {method.instruction && (
                  <p className="umra-method-instruction">{method.instruction}</p>
                )}
              </div>
            ))}
            {item.conclusion && (
              <div className="umra-methods-conclusion">{item.conclusion}</div>
            )}
          </div>
        );

      case 'separator':
        return <hr key={index} className="umra-separator" />;

      case 'conclusion-card':
        return (
          <div key={index} className="umra-conclusion-card">
            <div className="umra-conclusion-header">
              <div className="umra-conclusion-icon">🕋</div>
              <h3 className="umra-conclusion-title">{item.title}</h3>
            </div>
            <div className="umra-conclusion-main">
              <p className="umra-conclusion-message">{item.mainMessage}</p>
            </div>
            <div className="umra-conclusion-content">
              {item.content?.split('\n\n').map((paragraph, pIndex) => (
                <p key={pIndex}>{paragraph}</p>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="umra-guide-page">
      <Navigation />

      <SEO
        title="Guide complet de la 'Umra"
        description="Guide détaillé pour accomplir la 'Umra selon la Sunnah du Prophète ﷺ. Basé sur l'enseignement de Cheykh Souleymâne ar-Rouheylî avec invocations en arabe, phonétique et traduction."
        url="https://mahi-bayan.netlify.app/guide-umra"
      />

      <div className="umra-guide-content">
        <Header
          mainTitle={["GUIDE DE LA 'UMRA"]}
          subtitle={`Élaboration du guide en se basant sur la conférence de Cheykh Souleymâne El-Rouheylî
intitulé "خُذوا عَنِّي مَنَاسِكَكُمْ" au Masjid Nabawi le Samedi 6 Dhū al-Ḥijjah 1436 H`}
        />

        <main className="umra-guide-main">
          <div className="umra-guide-wrapper">
            {data.content.map((item, index) => renderContentItem(item, index))}
          </div>
        </main>

        <Footer texts={[]} />
      </div>
    </div>
  );
};

export default UmraGuidePage;